import { useEffect, useRef, useState } from 'react';

import { fetchPosSystems, type PosOption } from 'features/onboarding/api/options';

/**
 * Module-level cache so the POS list is fetched once per SPA session and shared
 * across mounts of the business-questions step.
 */
let cached: PosOption[] | null = null;
let inflight: Promise<PosOption[]> | null = null;

const load = (): Promise<PosOption[]> => {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetchPosSystems()
    .then((list) => {
      cached = list;
      return list;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

interface UsePosSystemsResult {
  data: PosOption[];
  isLoading: boolean;
  error: Error | null;
}

export const usePosSystems = (enabled: boolean): UsePosSystemsResult => {
  const [data, setData] = useState<PosOption[]>(cached ?? []);
  const [isLoading, setIsLoading] = useState<boolean>(enabled && !cached);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled || cached) return;
    setIsLoading(true);
    load()
      .then((list) => {
        if (mountedRef.current) setData(list);
      })
      .catch((err: unknown) => {
        if (mountedRef.current)
          setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    return () => {
      mountedRef.current = false;
    };
  }, [enabled]);

  return { data, isLoading, error };
};
